import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material'
import {
  Visibility,
  VisibilityOff
} from '@mui/icons-material'

const UserForm = ({ 
  user = null, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'CONSULTA',
    active: true
  })
  
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})

  // Initialize form data when user prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '', // Don't pre-fill password for editing
        role: user.role || 'CONSULTA',
        active: user.active !== undefined ? user.active : true
      })
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'CONSULTA',
        active: true
      })
    }
    setErrors({})
  }, [user])

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres'
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato de email inválido'
    }

    // Password validation (only for new users or when password is provided)
    if (!user && !formData.password.trim()) {
      newErrors.password = 'La contraseña es requerida'
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    
    if (!validateForm()) {
      return
    }

    const submitData = { ...formData }
    
    // Remove password if empty (for updates)
    if (user && !submitData.password.trim()) {
      delete submitData.password
    }

    // For new users, don't send active field (defaults to true on backend)
    if (!user) {
      delete submitData.active
    }

    onSubmit(submitData)
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Name Field */}
        <TextField
          label="Nombre completo"
          value={formData.name}
          onChange={handleChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          required
          fullWidth
          autoFocus={!user}
        />

        {/* Email Field */}
        <TextField
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          error={!!errors.email}
          helperText={errors.email}
          required
          fullWidth
        />

        {/* Password Field */}
        <TextField
          label={user ? "Nueva contraseña (opcional)" : "Contraseña"}
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange('password')}
          error={!!errors.password}
          helperText={errors.password || (user ? 'Dejar vacío para mantener la contraseña actual' : '')}
          required={!user}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleTogglePasswordVisibility}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Role Field */}
        <FormControl fullWidth>
          <InputLabel>Rol</InputLabel>
          <Select
            value={formData.role}
            onChange={handleChange('role')}
            label="Rol"
          >
            <MenuItem value="CONSULTA">Consulta</MenuItem>
            <MenuItem value="ADMINISTRADOR">Administrador</MenuItem>
          </Select>
        </FormControl>

        {/* Active Status (only for editing) */}
        {user && (
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={handleChange('active')}
                color="primary"
              />
            }
            label="Usuario activo"
          />
        )}

        {/* Role Information */}
        <Alert severity="info" sx={{ mt: 1 }}>
          <strong>Roles:</strong>
          <br />
          • <strong>Administrador:</strong> Acceso completo al sistema
          <br />
          • <strong>Consulta:</strong> Solo lectura de datos
        </Alert>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          justifyContent: 'flex-end',
          mt: 2
        }}>
          <Button
            type="button"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading 
              ? (user ? 'Actualizando...' : 'Creando...') 
              : (user ? 'Actualizar' : 'Crear')
            }
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default UserForm
